import React, { useEffect, useState } from "react"
import Box from "../../../components/Ui/Box"
import { TabText } from "./ManageOrder.styled"
import { useQuery } from "@apollo/client"
import GET_ORDER from "../../../graphql/queries/order/GetOrder"
import { useNavigate, useParams } from "react-router-dom"

import * as graphql from "lib/types/generated/graphql-types"
import { Tab, TabList, TabPanel, Tabs } from "react-tabs"
import PageHeader from "../../../components/Ui/Page/PageHeader"
import PrimaryButton from "../../../components/Ui/Buttons/PrimaryButton"
import CancelOrderPopup from "../../../components/Order/ManageOrder/CancelOrder/CancelOrderPopup"
import RefundOrder from "../../../components/Order/ManageOrder/RefundOrder/RefundOrder"
import DeliveryOrder from "../../../components/Order/ManageOrder/DeliverOrder/DeliveryOrder"
import CompensationOrder from "../../../components/Order/ManageOrder/CompensationOrder/CompensationOrder"
import ReleaseOrder from "../../../components/Order/ManageOrder/ReleaseOrder/ReleaseOrder"
import ModifyOrderLines from "../../../components/Order/ManageOrder/ModifyOrder/ModifyOrderLines/ModifyOrderLines"
import { Compensation } from "../../../classes/order/Compensation"
import { Delivery } from "../../../classes/order/Delivery"
import { ModificationOrderLines } from "../../../classes/order/ModificationOrderLines"
import { Refund } from "../../../classes/order/Refund"
import { Release } from "../../../classes/order/Release"
import { Cancellation } from "../../../classes/order/Cancellation"
import CancelOrder from "../../../components/Order/ManageOrder/CancelOrder/CancelOrder"
import ModifyShippingFees from "../../../components/Order/ManageOrder/ModifyOrder/ModifyShippingFees/ModifyShippingFees"
import { ModificationShippingFees } from "../../../classes/order/ModificationShippingFees"
import ALL_REASON_CODES from "../../../graphql/queries/settings/reasonCodes/AllReasonCodes"

const ManageOrder = () => {
  const { id } = useParams()
  const { data, refetch, loading } = useQuery(GET_ORDER, { variables: { id } })
  const [showCancelPopup, setShowCancelPopup] = useState(false)
  const navigate = useNavigate()

  const order = data?.Order?.details
  const orderStatus = order?.status as graphql.OrderStatus
  const orderHistory = data?.Order?.history as graphql.OrderHistory
  const modificationOrderLines = new ModificationOrderLines(order?.Modifications)
  const modificationShippingFees = new ModificationShippingFees(order?.Modifications)
  const deliveries = new Delivery(order?.Deliveries)
  const releases = new Release(order?.Releases)
  const refunds = new Refund(order?.Refunds)
  const compensations = new Compensation(order?.Compensations)
  const cancellations = new Cancellation(order?.Cancellations)

  const showDelivery = () =>
    !releases.completed() &&
    !cancellations.active() &&
    !modificationOrderLines.active() &&
    !modificationShippingFees.active() &&
    !releases.active() &&
    !refunds.active() &&
    !compensations.active() &&
    !deliveries.isOrderFullyDelivered(order)

  const showRelease = () =>
    !releases.completed() &&
    !orderStatus?.orderStates?.includes(graphql.OrderState.FullyDelivered) &&
    deliveries.completed() &&
    !deliveries.active() &&
    !refunds.onGoing() &&
    !refunds.pending() &&
    !refunds.failed() &&
    !compensations.onGoing() &&
    !compensations.pending() &&
    !compensations.failed()

  const showCompensation = () =>
    deliveries.completed() &&
    !deliveries.active() &&
    !releases.onGoing() &&
    !releases.pending() &&
    !releases.failed() &&
    !refunds.pending() &&
    !refunds.onGoing() &&
    !refunds.failed()
  const showRefund = () =>
    deliveries.completed() && !deliveries.active() && !releases.active() && !compensations.active()
  const showModifyOrderLines = () =>
    order &&
    !deliveries.completed() &&
    !cancellations.active() &&
    !deliveries.active() &&
    !modificationShippingFees.active()
  const showModifyShippingFees = () =>
    order &&
    order.shippingFees.length > 0 &&
    !deliveries.completed() &&
    !cancellations.active() &&
    !deliveries.active() &&
    !modificationOrderLines.active()

  const showCancellation = () =>
    !deliveries.completed() &&
    !deliveries.active() &&
    !modificationOrderLines.active() &&
    !modificationShippingFees.active() &&
    (cancellations.failed() || cancellations.pending() || cancellations.onGoing())

  const showCancellationButton = () =>
    !deliveries.completed() &&
    !deliveries.active() &&
    !modificationOrderLines.active() &&
    !modificationShippingFees.active() &&
    !cancellations.active()

  useEffect(() => {
    if (cancellations.completed()) {
      navigate(`/orders/${id}`)
    }
  }, [order?.Cancellations])

  const { data: reasonData } = useQuery(ALL_REASON_CODES)
  const customReasons = reasonData?.Order?.customReasonCodes?.codes

  return (
    <Box padding="0" margin="0">
      <PageHeader
        title={"Manage order #" + (order?.reference ?? "")}
        description={<>Manage order</>}
        goBackLinkUrl={`/orders/${id}`}
        goBackLinkText="Back to order"
        hasTabs
      >
        {showCancellationButton() && (
          <PrimaryButton mode="delete" handleClick={() => setShowCancelPopup(true)}>
            Cancel order
          </PrimaryButton>
        )}
      </PageHeader>
      <CancelOrderPopup
        setShowCancelPopup={setShowCancelPopup}
        showCancelPopup={showCancelPopup}
        order={order}
        refetch={refetch}
        refetchLoading={loading}
      />
      <div className={loading ? "blur" : ""}>
        <Tabs>
          <TabList>
            {showDelivery() && <Tab>Deliveries</Tab>}
            {showModifyOrderLines() && <Tab>Modify order lines</Tab>}
            {showModifyShippingFees() && <Tab>Modify shipping fees</Tab>}
            {deliveries.completed() && (
              <>
                {showRelease() && <Tab>Releases</Tab>}
                {showRefund() && <Tab>Refunds</Tab>}
                {showCompensation() && <Tab>Compensations</Tab>}
              </>
            )}
            {showCancellation() && (
              <Tab>
                <TabText>Cancellations</TabText>
              </Tab>
            )}
          </TabList>
          {showCancellation() && (
            <TabPanel>
              <CancelOrder
                order={order}
                refetch={refetch}
                orderHistory={orderHistory}
                setShowCancelPopup={setShowCancelPopup}
              />
            </TabPanel>
          )}
          {showDelivery() && (
            <TabPanel>
              <DeliveryOrder
                order={order}
                refetch={refetch}
                loading={loading}
                orderHistory={orderHistory}
                customReasons={customReasons}
              />
            </TabPanel>
          )}
          {showModifyOrderLines() && (
            <TabPanel>
              <ModifyOrderLines
                order={order}
                refetch={refetch}
                loading={loading}
                orderHistory={orderHistory}
                customReasons={customReasons}
              />
            </TabPanel>
          )}
          {showModifyShippingFees() && (
            <TabPanel>
              <ModifyShippingFees
                order={order}
                refetch={refetch}
                loading={loading}
                orderHistory={orderHistory}
                customReasons={customReasons}
              />
            </TabPanel>
          )}
          {showRelease() && (
            <TabPanel>
              <ReleaseOrder
                order={order}
                refetch={refetch}
                loading={loading}
                failedReleases={releases.failedReleases()}
                orderHistory={orderHistory}
              />
            </TabPanel>
          )}
          {showRefund() && (
            <TabPanel>
              <RefundOrder
                order={order}
                refetch={refetch}
                failedRefunds={refunds.failedRefunds()}
                loading={loading}
                orderHistory={orderHistory}
                customReasons={customReasons}
              />
            </TabPanel>
          )}
          {showCompensation() && (
            <TabPanel>
              <CompensationOrder
                order={order}
                refetch={refetch}
                failedCompensations={compensations.failedCompensations()}
                loading={loading}
                orderHistory={orderHistory}
                customReasons={customReasons}
              />
            </TabPanel>
          )}
        </Tabs>
      </div>
    </Box>
  )
}

export default ManageOrder
